import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import {
  approveBuild,
  approveSpec,
  cancelAiJob,
  CANCELABLE_SPEC_STATUSES,
  deleteUnwoundJob,
  BUILD_QUEUE_STATUSES,
  fetchQueueJobs,
  IN_PROGRESS_STATUSES,
  isOffQueueStatus,
  reorderQueueJobs,
  sendBuildBackToSpec,
  setAiJobModel,
  setAutoApprove,
  SPEC_QUEUE_STATUSES,
  submitApprovedBuild,
  type AiJob,
} from '../../lib/aiJobs';
import {
  AI_JOB_STATUS_LABEL,
  aiJobChipLabel,
  aiJobStatusTone,
  formatClaimElapsed,
  isAiJobInProgress,
  isClaimStale,
} from '../../lib/aiJobLabels';
import { uniqueChannelName } from '../../lib/realtime';
import { supabase } from '../../lib/supabase';
import { formatRelativeDate } from '../../utils/dateUtils';
import { Badge, Button, Card, Icon, IconButton, PulseDot, SegmentedControl, useToast } from '../ui';
import { useUser } from '../../context/UserContext';
import {
  fetchSubmitters,
  loadSubmitterFilter,
  matchesSubmitter,
  mergeVisibleOrder,
  saveSubmitterFilter,
  SUBMITTER_ALL,
  SUBMITTER_SCHEDULED,
  type Submitter,
  type SubmitterFilter,
} from '../../lib/aiJobSubmitters';
import DeleteConfirmationModal from '../clientDetail/sections/functions/DeleteConfirmationModal';
import { NoteNotSubmittedDialog } from './operator/SubmissionNote';
import { useSubmissionNote } from './operator/useSubmissionNote';
import ModelPicker from './ModelPicker';
import SummaryTags from './operator/SummaryTags';
import { operatorClientName, summaryChipTags } from '../../lib/aiJobSummary';
import { HandoffNoteField } from './HandoffNote';
import { handoffAuthor, handoffEntries, handoffTooltip } from '../../lib/aiJobHandoff';
import { specReviewBadge } from '../../lib/specReview';

interface AiQueueCardsProps {
  onOpenJob: (job: AiJob) => void;
}

/** Ambient per-status motion (demo set). One border/overlay recipe per row,
    plus an optional track element between the label and the badge. */
type RowFx = {
  rowClass?: string;
  rowStyle?: React.CSSProperties;
  track?: 'relay' | 'conveyor';
  trackDir?: 'back';
  trackTone?: string;
};

/** Tones always mirror aiJobStatusTone so motion and badge read as one signal. */
const QUEUE_LIMIT = 25;

/**
 * One queue's rows, with every job an agent holds right now pinned on top. The list is capped and ordered
 * by queue_position, so 23 builds waiting for review pushed the five claimed builds to rows 39-44 and the
 * card showed nothing being worked on (2026-10-12).
 */
async function fetchQueueWithClaimed(queue: 'spec' | 'build'): Promise<AiJob[]> {
  const statuses = queue === 'spec' ? SPEC_QUEUE_STATUSES : BUILD_QUEUE_STATUSES;
  const [rows, claimed] = await Promise.all([
    fetchQueueJobs({ queue, limit: QUEUE_LIMIT }),
    fetchQueueJobs({ status: IN_PROGRESS_STATUSES.filter((s) => statuses.includes(s)) }),
  ]);
  const pinned = new Set(claimed.map((job) => job.id));
  return [...claimed, ...rows.filter((job) => !pinned.has(job.id))];
}

function rowFx(status: AiJob['status'], stale: boolean): RowFx {
  switch (status) {
    // In progress — a comet orbits the row border (stale = slower orbit).
    case 'spec_in_progress':
    case 'approval_in_progress':
      return { rowClass: 'ck-anim-swirl ck-tone-info' };
    case 'build_in_progress':
      return { rowClass: `ck-anim-swirl ck-tone-info${stale ? ' ck-anim-swirl--slow' : ''}` };
    // Unwinding — conveyor dashes run back toward the label, in red.
    case 'unwind_in_progress':
      return { track: 'conveyor', trackDir: 'back', trackTone: 'ck-tone-down' };
    // Queued / revision — breathing left-edge beacon, amber like the badge.
    case 'spec_queued':
    case 'build_queued':
    case 'spec_revision':
    case 'build_revision':
    case 'unwind_queued':
      return { rowClass: 'ck-anim-beacon ck-tone-warn' };
    // Ready to delete — pipeline + bloom like the ready states, but red.
    case 'unwind_ready':
      return {
        rowClass: 'ck-anim-pipeline ck-anim-bloom ck-tone-down',
        rowStyle: { '--ck-pipe': '100%' } as React.CSSProperties,
      };
    // Parked behind another build.
    case 'build_staged':
      return { rowClass: 'ck-anim-hold' };
    // Approved — green beacon.
    case 'approved':
      return { rowClass: 'ck-anim-beacon ck-tone-up' };
    // Ready for you / shipped — green pipeline + bloom.
    case 'spec_ready':
    case 'build_ready':
    case 'complete':
      return {
        rowClass: 'ck-anim-pipeline ck-anim-bloom ck-tone-up',
        rowStyle: { '--ck-pipe': '100%' } as React.CSSProperties,
      };
    default:
      return {};
  }
}

function SortableJobRow({
  job,
  index,
  now,
  onOpenJob,
  onCancelJob,
  onDeleteJob,
  onSubmitJob,
  onSendBack,
  onApproveJob,
  onApproveBuild,
  onToggleAuto,
  onSetModel,
}: {
  job: AiJob;
  index: number;
  now: number;
  onOpenJob: (job: AiJob) => void;
  onCancelJob?: (job: AiJob) => void;
  onDeleteJob?: (job: AiJob) => void;
  onSubmitJob?: (job: AiJob) => void;
  onSendBack?: (job: AiJob) => void;
  onApproveJob?: (job: AiJob) => void;
  onApproveBuild?: (job: AiJob) => void;
  onToggleAuto?: (job: AiJob) => void;
  onSetModel?: (job: AiJob, model: string | null) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: job.id });
  const inProgress = isAiJobInProgress(job.status);
  const review = specReviewBadge(job);
  // Model is locked once an agent holds the job or it has shipped; before that
  // it can be re-pinned right from the row (takes effect on the next pickup).
  const modelLocked = inProgress || job.status === 'complete' || isOffQueueStatus(job.status);
  const isRevision = job.status.endsWith('_revision');
  const isStaged = job.status === 'build_staged';
  const waitingOnAgent = job.status.endsWith('_queued') || isRevision || job.status === 'approved';
  const stale = inProgress && isClaimStale(job.claimed_at, now);
  const fx = rowFx(job.status, stale);
  const clientName = operatorClientName(job);
  const elapsed = inProgress ? formatClaimElapsed(job.claimed_at, now) : '';
  const subtitle = inProgress
    ? [job.claimed_by || 'Agent', elapsed, stale ? 'stale' : ''].filter(Boolean).join(' · ')
    : isStaged
      ? job.lock_reason || 'Waiting on another build lock'
      : job.status === 'approved'
        ? 'Waiting for an agent to ship live'
        : job.status === 'complete'
          ? 'Approval report ready — send to the client, then Submitted'
          : [
              formatRelativeDate(job.updated_at),
              job.queue_note
                ? job.queue_note.length > 80
                  ? `${job.queue_note.slice(0, 77)}…`
                  : job.queue_note
                : '',
            ]
              .filter(Boolean)
              .join(' · ');

  return (
    <div
      ref={setNodeRef}
      // Phones: the label gets the whole row and the chips/actions drop to a second line under it.
      className={`hover-wash pressable ck-ff-row flex-wrap md:flex-nowrap py-[10px] md:py-[8px] ${fx.rowClass ?? ''}`}
      onClick={() => onOpenJob(job)}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        paddingLeft: '10px',
        paddingRight: '10px',
        margin: '0 -10px',
        borderRadius: 'var(--radius-sm)',
        cursor: 'pointer',
        opacity: isDragging ? 0.35 : 1,
        zIndex: isDragging ? 10 : 'auto',
        position: 'relative',
        ...fx.rowStyle,
      }}
      {...attributes}
      {...listeners}
    >
      <span className="ck-ff-order" style={{ position: 'relative', textAlign: 'center', width: '28px', flexShrink: 0 }}>
        <span className="t-num ck-ff-idx" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
          {String(index + 1).padStart(2, '0')}
        </span>
        <span
          className="ck-ff-grip"
          title="Drag to reorder"
          style={{
            position: 'absolute',
            inset: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-faint)',
            cursor: 'grab',
          }}
        >
          <Icon name="grip-vertical" size={12} />
        </span>
      </span>
      <PulseDot
        tone={
          inProgress
            ? job.status === 'unwind_in_progress'
              ? 'down'
              : 'info'
            : isRevision
              ? 'warn'
              : waitingOnAgent
                ? 'info'
                : 'idle'
        }
        pulse={inProgress}
        title={
          inProgress
            ? stale
              ? 'In progress, sitting too long'
              : AI_JOB_STATUS_LABEL[job.status]
            : waitingOnAgent
              ? 'Waiting on agent'
              : AI_JOB_STATUS_LABEL[job.status]
        }
      />
      <span className="min-w-0 flex-1">
        <span
          className="md:truncate"
          style={{
            display: 'block',
            fontSize: 'var(--text-sm)',
            color: 'var(--text-body)',
          }}
        >
          {aiJobChipLabel(job)}
        </span>
        <span style={{ display: 'block', fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
          <span style={{ color: clientName ? 'var(--text-muted)' : 'var(--text-faint)' }}>
            {clientName ?? 'No client'}
          </span>
          {subtitle ? ` · ${subtitle}` : null}
        </span>
        {handoffEntries(job).length ? (
          <span
            title={handoffTooltip(job)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              marginTop: '2px',
              fontSize: 'var(--text-xs)',
              color: 'var(--text-muted)',
              minWidth: 0,
            }}
          >
            <Icon name="sticky-note" size={11} style={{ color: 'var(--accent-text)', flexShrink: 0 }} />
            {/* The latest reminder on the card; the tooltip and the job panel have them all. */}
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {handoffEntries(job).length > 1 ? `${handoffEntries(job).length} notes · ` : ''}
              {handoffEntries(job).at(-1)?.note}
            </span>
          </span>
        ) : null}
        <SummaryTags tags={summaryChipTags(job)} style={{ marginTop: '3px' }} />
      </span>
      {/* display: contents from md up, so desktop lays these out in the row exactly as before. */}
      <span className="flex w-full flex-wrap items-center gap-[6px] md:contents">
      {modelLocked ? (
        job.model ? <ModelPicker value={job.model} /> : null
      ) : onSetModel ? (
        <ModelPicker value={job.model} onChange={(model) => onSetModel(job, model)} />
      ) : null}
      {fx.track ? (
        <span
          aria-hidden
          className={`${fx.track === 'relay' ? 'ck-anim-relay' : 'ck-anim-conveyor'} ${fx.trackTone ?? ''}`}
          data-dir={fx.trackDir}
        />
      ) : null}
      <Badge tone={aiJobStatusTone(job.status)} dot={inProgress} pulse={inProgress}>
        {AI_JOB_STATUS_LABEL[job.status]}
      </Badge>
      {review ? (
        <Badge tone={review.tone} dot={review.pulse} pulse={review.pulse} title={review.title}>
          {review.label}
        </Badge>
      ) : null}
      <span aria-hidden className="flex-1 md:hidden" />
      {onToggleAuto && job.status === 'spec_queued' && job.task_type !== 'security_audit' ? (
        <IconButton
          icon="zap"
          size="sm"
          label={job.auto_approve ? 'Auto-approve on — spec skips review' : 'Auto-approve — spec skips review'}
          style={job.auto_approve ? { color: 'var(--accent-text)', background: 'var(--accent-soft)' } : undefined}
          onClick={(e) => {
            e.stopPropagation();
            onToggleAuto(job);
          }}
        />
      ) : null}
      {onCancelJob && job.status !== 'spec_ready' && CANCELABLE_SPEC_STATUSES.includes(job.status) ? (
        <IconButton
          icon="x"
          size="sm"
          label="Cancel"
          onClick={(e) => {
            e.stopPropagation();
            onCancelJob(job);
          }}
        />
      ) : null}
      {onApproveJob && job.status === 'spec_ready' ? (
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            onApproveJob(job);
          }}
        >
          Approve
        </Button>
      ) : null}
      {onApproveBuild && job.status === 'build_ready' ? (
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            onApproveBuild(job);
          }}
        >
          Approve
        </Button>
      ) : null}
      {onDeleteJob && job.status === 'unwind_ready' ? (
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            onDeleteJob(job);
          }}
        >
          Delete
        </Button>
      ) : null}
      {onSubmitJob && job.status === 'complete' ? (
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            onSubmitJob(job);
          }}
        >
          Submitted
        </Button>
      ) : null}
      {onSendBack && job.status === 'build_queued' ? (
        <IconButton
          icon="x"
          size="sm"
          label="Send back to spec"
          onClick={(e) => {
            e.stopPropagation();
            onSendBack(job);
          }}
        />
      ) : null}
      </span>
    </div>
  );
}

function QueueCard({
  eyebrow,
  title,
  jobs,
  allJobs = jobs,
  empty,
  now,
  onOpenJob,
  onReorder,
  onCancelJob,
  onDeleteJob,
  onSubmitJob,
  onSendBack,
  onApproveJob,
  onApproveBuild,
  onToggleAuto,
  onSetModel,
}: {
  eyebrow: string;
  title: string;
  jobs: AiJob[];
  /** Whole queue, when `jobs` is filtered: row numbers and the count use it. */
  allJobs?: AiJob[];
  empty: string;
  now: number;
  onOpenJob: (job: AiJob) => void;
  onReorder: (jobs: AiJob[]) => void;
  onCancelJob?: (job: AiJob) => void;
  onDeleteJob?: (job: AiJob) => void;
  onSubmitJob?: (job: AiJob) => void;
  onSendBack?: (job: AiJob) => void;
  onApproveJob?: (job: AiJob) => void;
  onApproveBuild?: (job: AiJob) => void;
  onToggleAuto?: (job: AiJob) => void;
  onSetModel?: (job: AiJob, model: string | null) => void;
}) {
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    // Long-press to drag on touch so list scrolling stays scrolling.
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!active || !over || active.id === over.id) return;
    const oldIndex = jobs.findIndex((job) => job.id === active.id);
    const newIndex = jobs.findIndex((job) => job.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    onReorder(arrayMove([...jobs], oldIndex, newIndex));
  };

  return (
    <Card
      eyebrow={eyebrow}
      title={title}
      actions={
        allJobs.length > 0 ? (
          <span className="t-num" style={{ fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
            {jobs.length === allJobs.length ? jobs.length : `${jobs.length} of ${allJobs.length}`}
          </span>
        ) : undefined
      }
    >
      {jobs.length === 0 ? (
        <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>{empty}</p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={jobs.map((job) => job.id)} strategy={verticalListSortingStrategy}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {jobs.map((job) => (
                <SortableJobRow
                  key={job.id}
                  job={job}
                  index={allJobs.indexOf(job)}
                  now={now}
                  onOpenJob={onOpenJob}
                  onCancelJob={onCancelJob}
                  onDeleteJob={onDeleteJob}
                  onSubmitJob={onSubmitJob}
                  onSendBack={onSendBack}
                  onApproveJob={onApproveJob}
                  onApproveBuild={onApproveBuild}
                  onToggleAuto={onToggleAuto}
                  onSetModel={onSetModel}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </Card>
  );
}

export default function AiQueueCards({ onOpenJob }: AiQueueCardsProps) {
  const [specJobs, setSpecJobs] = useState<AiJob[]>([]);
  const [buildJobs, setBuildJobs] = useState<AiJob[]>([]);
  const [pending, setPending] = useState<AiJob | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AiJob | null>(null);
  const [pendingSubmit, setPendingSubmit] = useState<AiJob | null>(null);
  // A client feedback job whose client note isn't on the client's feedback asks first (Submit note / without / Cancel).
  const submitNote = useSubmissionNote(pendingSubmit);
  const [pendingApproveSpec, setPendingApproveSpec] = useState<AiJob | null>(null);
  const [pendingApproveBuild, setPendingApproveBuild] = useState<AiJob | null>(null);
  const [cancelling, setCancelling] = useState(false);
  /** Optional note typed in the approve dialog; rides with the click. */
  const [handoffDraft, setHandoffDraft] = useState('');
  const [now, setNow] = useState(() => Date.now());
  const reorderingRef = useRef(false);
  const toast = useToast();
  const { user } = useUser();
  const [submitter, setSubmitter] = useState<SubmitterFilter>(loadSubmitterFilter);
  const [submitters, setSubmitters] = useState<Submitter[]>([]);

  useEffect(() => {
    fetchSubmitters()
      .then(setSubmitters)
      .catch((err) => console.error('Error loading job submitters:', err));
  }, []);

  const load = async () => {
    const [spec, build] = await Promise.all([
      fetchQueueWithClaimed('spec'),
      fetchQueueWithClaimed('build'),
    ]);
    setSpecJobs(spec);
    setBuildJobs(build);
  };

  useEffect(() => {
    let cancelled = false;

    const tick = async () => {
      if (reorderingRef.current) return;
      try {
        const [spec, build] = await Promise.all([
          fetchQueueWithClaimed('spec'),
          fetchQueueWithClaimed('build'),
        ]);
        if (cancelled || reorderingRef.current) return;
        setSpecJobs(spec);
        setBuildJobs(build);
      } catch (err) {
        console.error('Error loading agent queues:', err);
      }
    };

    tick();
    const channel = supabase
      .channel(uniqueChannelName('ai-queue-cards'))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'ai_jobs' }, tick)
      .subscribe();
    const poll = window.setInterval(tick, 30000);
    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
      window.clearInterval(poll);
    };
  }, []);

  const hasInProgress =
    specJobs.some((job) => isAiJobInProgress(job.status)) ||
    buildJobs.some((job) => isAiJobInProgress(job.status));

  useEffect(() => {
    if (!hasInProgress) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [hasInProgress]);

  const handleReorder = async (queue: 'spec' | 'build', visibleNext: AiJob[]) => {
    reorderingRef.current = true;
    const next = mergeVisibleOrder(queue === 'spec' ? specJobs : buildJobs, visibleNext);
    if (queue === 'spec') setSpecJobs(next);
    else setBuildJobs(next);
    try {
      await reorderQueueJobs(next.map((job) => job.id));
    } catch (err) {
      console.error('Error updating queue order:', err);
      try {
        await load();
      } catch (reloadErr) {
        console.error('Error reloading agent queues:', reloadErr);
      }
    } finally {
      reorderingRef.current = false;
    }
  };

  const handleConfirmCancel = async () => {
    if (!pending || cancelling) return;
    setCancelling(true);
    try {
      await cancelAiJob(pending.id);
      setPending(null);
      await load();
    } catch (err) {
      console.error('Error cancelling spec:', err);
    } finally {
      setCancelling(false);
    }
  };

  const handleConfirmApproveSpec = async () => {
    if (!pendingApproveSpec || cancelling) return;
    setCancelling(true);
    try {
      await approveSpec(pendingApproveSpec.id, {}, { note: handoffDraft, by: handoffAuthor(user) });
      setPendingApproveSpec(null);
      setHandoffDraft('');
      await load();
    } catch (err) {
      console.error('Error approving spec:', err);
    } finally {
      setCancelling(false);
    }
  };

  const handleConfirmApproveBuild = async () => {
    if (!pendingApproveBuild || cancelling) return;
    setCancelling(true);
    try {
      await approveBuild(pendingApproveBuild.id, { note: handoffDraft, by: handoffAuthor(user) });
      setPendingApproveBuild(null);
      setHandoffDraft('');
      await load();
    } catch (err) {
      console.error('Error approving build:', err);
    } finally {
      setCancelling(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete || cancelling) return;
    setCancelling(true);
    try {
      await deleteUnwoundJob(pendingDelete.id);
      setPendingDelete(null);
      await load();
    } catch (err) {
      console.error('Error deleting unwound job:', err);
    } finally {
      setCancelling(false);
    }
  };

  const handleSendBack = async (job: AiJob) => {
    if (cancelling) return;
    setCancelling(true);
    try {
      await sendBuildBackToSpec(job.id);
      await load();
    } catch (err) {
      console.error('Error sending build back to spec:', err);
    } finally {
      setCancelling(false);
    }
  };

  const handleToggleAuto = async (job: AiJob) => {
    if (cancelling) return;
    setCancelling(true);
    try {
      await setAutoApprove(job.id, !job.auto_approve);
      await load();
    } catch (err) {
      console.error('Error toggling auto-approve:', err);
    } finally {
      setCancelling(false);
    }
  };

  /** Optimistic: the chip flips at once; a failed write reverts it and toasts. */
  const handleSetModel = async (job: AiJob, model: string | null) => {
    if ((job.model ?? null) === (model ?? null)) return;
    const apply = (value: string | null) => (rows: AiJob[]) =>
      rows.map((row) => (row.id === job.id ? { ...row, model: value } : row));
    setSpecJobs(apply(model));
    setBuildJobs(apply(model));
    try {
      await setAiJobModel(job.id, model);
    } catch (err) {
      console.error('Error setting job model:', err);
      setSpecJobs(apply(job.model ?? null));
      setBuildJobs(apply(job.model ?? null));
      toast.error('Model not saved. Reverted.', err instanceof Error ? err.message : undefined);
    }
  };

  const chooseSubmitter = (id: string) => {
    setSubmitter(id);
    saveSubmitterFilter(id);
  };

  // All · Mine · everyone else who submits · Scheduled (sweeps have no submitter).
  const filterOptions = useMemo(() => {
    const queued = [...specJobs, ...buildJobs];
    const count = (filter: SubmitterFilter) => queued.filter((job) => matchesSubmitter(job, filter)).length;
    const option = (id: string, label: string) => ({
      id,
      label: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          {label}
          <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
            {count(id)}
          </span>
        </span>
      ),
    });
    const others = submitters.filter((s) => s.id !== user?.id);
    return [
      option(SUBMITTER_ALL, 'All'),
      ...(user ? [option(user.id, 'Mine')] : []),
      ...others.map((s) => option(s.id, s.name)),
      option(SUBMITTER_SCHEDULED, 'Scheduled'),
    ];
  }, [specJobs, buildJobs, submitters, user]);

  // A remembered filter for someone no longer listed falls back to All.
  const activeFilter = filterOptions.some((o) => o.id === submitter) ? submitter : SUBMITTER_ALL;
  const filtered = activeFilter !== SUBMITTER_ALL;
  const visibleSpec = filtered ? specJobs.filter((job) => matchesSubmitter(job, activeFilter)) : specJobs;
  const visibleBuild = filtered ? buildJobs.filter((job) => matchesSubmitter(job, activeFilter)) : buildJobs;
  const whose =
    activeFilter === user?.id
      ? 'None of your jobs'
      : activeFilter === SUBMITTER_SCHEDULED
        ? 'No scheduled jobs'
        : `None of ${submitters.find((s) => s.id === activeFilter)?.name ?? 'their'}’s jobs`;

  const handleConfirmSubmit = async () => {
    if (!pendingSubmit || cancelling) return;
    setCancelling(true);
    try {
      await submitApprovedBuild(pendingSubmit.id);
      setPendingSubmit(null);
      await load();
    } catch (err) {
      console.error('Error marking submitted:', err);
    } finally {
      setCancelling(false);
    }
  };

  return (
    <>
      <div
        className="xl:col-span-2"
        style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', overflowX: 'auto' }}
      >
        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Submitted by</span>
        <SegmentedControl options={filterOptions} value={activeFilter} onChange={chooseSubmitter} style={{ flexShrink: 0 }} />
      </div>
      {/* Spec on the left, build on the right (reading order). Agents still pick builds first. */}
      <QueueCard
        eyebrow="Agent Spec Queue"
        title="Waiting on spec"
        jobs={visibleSpec}
        allJobs={specJobs}
        empty={filtered ? `${whose} in the spec queue.` : 'Nothing in the spec queue.'}
        now={now}
        onOpenJob={onOpenJob}
        onReorder={(next) => {
          void handleReorder('spec', next);
        }}
        onCancelJob={setPending}
        onApproveJob={(job) => {
          setHandoffDraft('');
          setPendingApproveSpec(job);
        }}
        onToggleAuto={handleToggleAuto}
        onSetModel={handleSetModel}
      />
      <QueueCard
        eyebrow="Agent Build Queue"
        title="Waiting on build"
        jobs={visibleBuild}
        allJobs={buildJobs}
        empty={filtered ? `${whose} in the build queue.` : 'Nothing in the build queue.'}
        now={now}
        onOpenJob={onOpenJob}
        onReorder={(next) => {
          void handleReorder('build', next);
        }}
        onDeleteJob={setPendingDelete}
        onSubmitJob={setPendingSubmit}
        onSendBack={handleSendBack}
        onApproveBuild={(job) => {
          setHandoffDraft('');
          setPendingApproveBuild(job);
        }}
        onSetModel={handleSetModel}
      />
      <DeleteConfirmationModal
        isOpen={!!pending}
        onClose={() => {
          if (!cancelling) setPending(null);
        }}
        onConfirm={handleConfirmCancel}
        isDeleting={cancelling}
        title="Cancel this spec?"
        message={pending ? `This pulls “${pending.title}” off the spec queue. You can enqueue it again later.` : ''}
        confirmButtonLabel="Cancel spec"
        processingLabel="Cancelling…"
      />
      <DeleteConfirmationModal
        isOpen={!!pendingApproveSpec}
        onClose={() => {
          if (!cancelling) setPendingApproveSpec(null);
        }}
        onConfirm={handleConfirmApproveSpec}
        isDeleting={cancelling}
        tone="accent"
        titleIcon={faCheck}
        title="Approve this spec?"
        message={
          pendingApproveSpec
            ? `“${pendingApproveSpec.title}” moves to the build queue — or stages behind this client's active build.`
            : ''
        }
        confirmButtonLabel="Approve to Build"
        processingLabel="Approving…"
      >
        <HandoffNoteField value={handoffDraft} onChange={setHandoffDraft} disabled={cancelling} />
      </DeleteConfirmationModal>
      <DeleteConfirmationModal
        isOpen={!!pendingApproveBuild}
        onClose={() => {
          if (!cancelling) setPendingApproveBuild(null);
        }}
        onConfirm={handleConfirmApproveBuild}
        isDeleting={cancelling}
        tone="accent"
        titleIcon={faCheck}
        title="Approve this build?"
        message={
          pendingApproveBuild
            ? `“${pendingApproveBuild.title}” stays on the board as Approved — an agent merges, migrates, and deploys, then writes the approval report.`
            : ''
        }
        confirmButtonLabel="Approve build"
        processingLabel="Approving…"
      >
        <HandoffNoteField value={handoffDraft} onChange={setHandoffDraft} disabled={cancelling} />
      </DeleteConfirmationModal>
      <DeleteConfirmationModal
        isOpen={!!pendingDelete}
        onClose={() => {
          if (!cancelling) setPendingDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        isDeleting={cancelling}
        title="Delete this job?"
        message={
          pendingDelete
            ? `This permanently deletes “${pendingDelete.title}” after unwind. Only do this if the unwind report looks right.`
            : ''
        }
        confirmButtonLabel="Delete job"
        processingLabel="Deleting…"
      />
      <NoteNotSubmittedDialog
        open={!!pendingSubmit && submitNote.needsGate}
        control={submitNote}
        busy={cancelling}
        onClose={() => {
          if (!cancelling) setPendingSubmit(null);
        }}
        onSubmitted={handleConfirmSubmit}
      />
      <DeleteConfirmationModal
        isOpen={!!pendingSubmit && submitNote.loaded && !submitNote.needsGate}
        onClose={() => {
          if (!cancelling) setPendingSubmit(null);
        }}
        onConfirm={handleConfirmSubmit}
        isDeleting={cancelling}
        title="Mark this submitted?"
        message={
          pendingSubmit
            ? `This takes “${pendingSubmit.title}” off the build queue and checks the linked comm chip if there is one. Path locks already dropped at complete, so a waiting build can already be in.`
            : ''
        }
        confirmButtonLabel="Mark submitted"
        processingLabel="Submitting…"
      />
    </>
  );
}
